import { Column, Img, Link, Row, Section, Text } from "@react-email/components";
import useThemeContextualColors, {
  sectionToParent,
} from "../../hooks/use-theme-contextual-colors";
import { tLayout } from "../../translations";
import type { CrossSellBlockProps } from "./types";

type CrossSellProduct = {
  id: string;
  handle: string | null;
  title: string;
  imageUrl: string | null;
  url: string;
};

function ProductCard({
  product,
  imgSize,
  textColor,
}: {
  product: CrossSellProduct;
  imgSize: number;
  textColor: string;
}) {
  return (
    <Link href={product.url} style={{ textDecoration: "none" }}>
      {product.imageUrl && (
        <Img
          src={product.imageUrl}
          alt={product.title}
          width={imgSize}
          height={imgSize}
          style={{
            width: `${imgSize}px`,
            height: `${imgSize}px`,
            objectFit: "cover",
            borderRadius: "8px",
            display: "block",
            margin: "0 auto",
          }}
        />
      )}
      <Text
        style={{
          margin: "6px 0 2px",
          fontSize: "12px",
          fontWeight: 600,
          color: textColor,
          textAlign: "center",
        }}
      >
        {product.title}
      </Text>
    </Link>
  );
}

export function CrossSellEmailRenderer({
  block,
  parcel,
  theme,
  section,
  locale,
  defaultLocale,
}: CrossSellBlockProps) {
  const colors = useThemeContextualColors(theme, sectionToParent(section));
  const count = block?.properties?.count ?? 2;
  const t = tLayout(locale, defaultLocale);

  const recommendations = parcel?.crossSellRecommendations as CrossSellProduct[] | null | undefined;

  if (!recommendations?.length) return null;

  const items = recommendations.slice(0, count);
  const imgSize = count <= 2 ? 100 : 80;

  return (
    <Section>
      <Row>
        <Column style={{ padding: "4px 4px 8px" }}>
          <Text className="m-0 p-0 leading-[1.4] font-semibold" style={{ color: colors.text }}>
            {t("crossSell.youMightAlsoLike")}
          </Text>
        </Column>
      </Row>

      {count === 1 && (
        <Row>
          <Column style={{ width: "100%", padding: "4px" }}>
            <ProductCard product={items[0]} imgSize={imgSize} textColor={colors.text} />
          </Column>
        </Row>
      )}

      {count === 2 && (
        <Row>
          {items.map((product) => (
            <Column key={product.id} style={{ width: "50%", verticalAlign: "top", padding: "4px" }}>
              <ProductCard product={product} imgSize={imgSize} textColor={colors.text} />
            </Column>
          ))}
        </Row>
      )}

      {count === 3 && (
        <Row>
          <Column style={{ width: "50%", verticalAlign: "top", padding: "4px" }}>
            <ProductCard product={items[0]} imgSize={imgSize} textColor={colors.text} />
            <div style={{ height: "8px" }} />
            <ProductCard product={items[1]} imgSize={imgSize} textColor={colors.text} />
          </Column>
          <Column style={{ width: "50%", verticalAlign: "top", padding: "4px" }}>
            <ProductCard product={items[2]} imgSize={imgSize} textColor={colors.text} />
          </Column>
        </Row>
      )}

      {count === 4 && (
        <>
          <Row>
            {items.slice(0, 2).map((product) => (
              <Column key={product.id} style={{ width: "50%", verticalAlign: "top", padding: "4px" }}>
                <ProductCard product={product} imgSize={imgSize} textColor={colors.text} />
              </Column>
            ))}
          </Row>
          <Row>
            {items.slice(2, 4).map((product) => (
              <Column key={product.id} style={{ width: "50%", verticalAlign: "top", padding: "4px" }}>
                <ProductCard product={product} imgSize={imgSize} textColor={colors.text} />
              </Column>
            ))}
          </Row>
        </>
      )}
    </Section>
  );
}
